import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Tag from "@/components/base/Tag";
import { activeUsers, categories, posts, weeklyHot } from "@/mocks/posts";

interface LatestFeedProps {
  searchQuery: string;
}

export default function LatestFeed({ searchQuery }: LatestFeedProps) {
  const [activeCategory, setActiveCategory] = useState("all");

  const filters = [{ key: "all", name: "全部" }, ...categories.map((item) => ({ key: item.key, name: item.name }))];

  const filteredPosts = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    return posts.filter((post) => {
      const matchCategory = activeCategory === "all" || post.categoryKey === activeCategory;
      const matchKeyword =
        !keyword ||
        post.title.toLowerCase().includes(keyword) ||
        post.excerpt.toLowerCase().includes(keyword) ||
        post.category.toLowerCase().includes(keyword) ||
        post.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, searchQuery]);

  return (
    <section id="feed" className="mx-auto w-full max-w-container scroll-mt-24 px-4 md:px-6 py-16 md:py-20">
      <SectionHeading
        eyebrow="Latest"
        title="最新爆料"
        desc="刚刚发生的、正在讨论的，都在这里按时间排好队。"
      />

      <div className="mt-8 flex flex-wrap gap-2">
        {filters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => setActiveCategory(filter.key)}
            className={`rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer whitespace-nowrap ${
              activeCategory === filter.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {filter.name}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {filteredPosts.length === 0 ? (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">暂时没有匹配的线索</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                换一个关键词试试，或者切换上方的分类。没有找到想要的？不如你来投一条。
              </p>
              <a
                href="#submit"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-quill-pen-line text-base" aria-hidden="true" />
                投稿一条线索
              </a>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <a href={`#post-${post.id}`} className="flex cursor-pointer flex-col sm:flex-row">
                    <div className="h-48 w-full shrink-0 overflow-hidden bg-background-200 sm:h-auto sm:w-56 md:w-64">
                      <img
                        src={post.cover}
                        alt={`${post.title} 的配图`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-5 md:p-6">
                      <div className="flex flex-wrap items-center gap-2">
                        <Tag>{post.category}</Tag>
                        {post.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="font-label text-xs text-foreground-500">
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="mt-3 font-heading text-lg md:text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                        {post.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.excerpt}</p>
                      <div className="mt-auto flex flex-wrap items-center gap-4 pt-4 font-label text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-user-3-line text-base" aria-hidden="true" />
                          {post.author}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-time-line text-base" aria-hidden="true" />
                          {post.time}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-primary-600">
                          <i className="ri-chat-3-line text-base" aria-hidden="true" />
                          {post.comments} 条讨论
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-thumb-up-line text-base" aria-hidden="true" />
                          {post.likes}
                        </span>
                      </div>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-4" aria-label="侧边栏">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <div className="flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-lg text-primary-600" aria-hidden="true" />
              <h3 className="font-heading text-lg font-bold text-foreground-950">本周热榜</h3>
            </div>
            <ol className="mt-5 space-y-4">
              {weeklyHot.map((item) => (
                <li key={item.rank}>
                  <a href={`#post-${item.id}`} className="flex cursor-pointer items-start gap-3">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-label text-xs font-bold ${
                        item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-300 text-foreground-700"
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block line-clamp-2 font-label text-sm leading-snug text-foreground-800 transition-colors hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-1 block font-label text-xs text-foreground-500">{item.comments} 条讨论</span>
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex items-center gap-2">
              <i className="ri-user-star-line text-lg text-accent-600" aria-hidden="true" />
              <h3 className="font-heading text-lg font-bold text-foreground-950">活跃的身影</h3>
            </div>
            <ul className="mt-5 space-y-4">
              {activeUsers.map((user) => (
                <li key={user.name} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-900">
                    {user.avatar}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-label text-sm font-medium text-foreground-900">{user.name}</span>
                    <span className="block truncate font-label text-xs text-foreground-500">{user.note}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-primary-500 p-6 text-background-50">
            <h3 className="font-heading text-lg font-bold">手里有料？</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-100/90">
              把你看到的写下来，一条线索也许就能帮到很多同样好奇的人。
            </p>
            <a
              href="#submit"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-background-50 px-4 py-2.5 font-label text-sm font-medium text-primary-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-quill-pen-line text-base" aria-hidden="true" />
              立即投稿
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}